// Dependency Graph Builder — public barrel and orchestration entry point
// (ARCHITECTURE.md §7).
//
// `buildDependencyGraph` ties the layer together: resolve manifest edges →
// build the file→artifact mapping → assemble the artifact-aware graph with
// boundary-crossing edges. It is the only place in this layer that touches the
// filesystem (reading package.json manifests); the actual graph assembly is the
// pure `buildDependencyGraph` core in ./graph-builder.js.

import { readFile } from "node:fs/promises";
import { isAbsolute, join } from "node:path";

import type { AstGraph } from "../ast/types.js";
import { buildArtifactMapping } from "./artifact-mapper.js";
import { buildDependencyGraph as assembleDependencyGraph } from "./graph-builder.js";
import { resolveManifestEdges } from "./manifest-resolver.js";
import type { GraphEdge } from "../types.js";
import type { DependencyGraph } from "./types.js";

export * from "./types.js";
export * from "./manifest-resolver.js";
export * from "./artifact-mapper.js";
export type {
  BuildDependencyGraphInput,
  ResolveSymbolEdgesInput,
} from "./graph-builder.js";
export { resolveSymbolEdges } from "./graph-builder.js";

export interface BuildDependencyGraphOptions {
  /** The AST graph whose file nodes and edges form the structural base. */
  ast: AstGraph;
  /** Project root; manifest paths and graph node ids are relative to it. */
  projectRoot: string;
  /** artifactId → glob patterns (the DSL `path_patterns`). */
  artifactPatterns: Map<string, string[]>;
  /**
   * Paths to package.json manifests (absolute, or relative to `projectRoot`).
   * Each contributes external-package `depends_on` edges (§7.3).
   */
  manifests?: string[];
}

/**
 * Read and resolve the dependency edges from every configured manifest. A
 * manifest that cannot be read or parsed is skipped with a diagnostic rather
 * than aborting the whole build (matching the AST layer's resilience, §6.7).
 */
async function resolveAllManifestEdges(
  projectRoot: string,
  manifests: string[],
): Promise<GraphEdge[]> {
  const edges: GraphEdge[] = [];

  for (const manifestPath of manifests) {
    const absPath = isAbsolute(manifestPath)
      ? manifestPath
      : join(projectRoot, manifestPath);

    let parsed: Record<string, unknown>;
    try {
      const content = await readFile(absPath, "utf8");
      parsed = JSON.parse(content) as Record<string, unknown>;
    } catch (error) {
      console.warn(
        `[artifact-contracts-analyzer] could not read manifest '${manifestPath}': ${
          (error as Error).message
        }; skipping.`,
      );
      continue;
    }

    edges.push(...resolveManifestEdges(manifestPath, parsed));
  }

  return edges;
}

/**
 * Build the Dependency Graph for a project (ARCHITECTURE.md §7).
 *
 * Steps: resolve manifest edges from package.json files → build the
 * file→artifact mapping from the AST file nodes → assemble the artifact-aware
 * DependencyGraph (with boundary-crossing edges) → return it.
 */
export async function buildDependencyGraph(
  options: BuildDependencyGraphOptions,
): Promise<DependencyGraph> {
  const { ast, projectRoot, artifactPatterns, manifests = [] } = options;

  // 1. Manifest edges (external packages).
  const manifestEdges = await resolveAllManifestEdges(projectRoot, manifests);

  // 2. Artifact mapping over every file node in the AST graph.
  const files: string[] = [];
  for (const node of ast.nodes.values()) {
    if (node.kind === "file") files.push(node.filePath);
  }
  const artifactMapping = buildArtifactMapping(files, artifactPatterns);

  // 3. Assemble the graph (pure), including boundary-crossing edges.
  return assembleDependencyGraph({ ast, manifestEdges, artifactMapping });
}
